import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import { Button, FormControl, Grid, Typography } from '@mui/material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { useState } from 'react';

import {
  selectUnidadeSelecionada,
  selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { generateProcessosExportacaoReport } from './processosExportacaoPage.helpers';

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '50vw',
      minHeight: '40vh',
    },
  },
});

type PeriodoKey = 'registroDue' | 'desembaraco' | 'averbacao';

const PERIODOS: { key: PeriodoKey; label: string }[] = [
  { key: 'registroDue', label: 'Data Registro DU-E' },
  { key: 'desembaraco', label: 'Data Desembaraço' },
  { key: 'averbacao', label: 'Data Averbação' },
];

const periodoInicial = { inicio: '', fim: '' };

const ProcessosExportacaoPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  keycloak.updateToken(300);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [periodos, setPeriodos] = useState<Record<PeriodoKey, { inicio: string; fim: string }>>({
    registroDue: periodoInicial,
    desembaraco: periodoInicial,
    averbacao: periodoInicial,
  });

  useUnidadeNegocioGuard();

  const handleTenantsChange = (_e: any, value: SimpleMultiSelectOption[] | null) =>
    setSelectedTenants(value || []);

  const handleClientsChange = (_e: any, value: SimpleMultiSelectOption[] | null) =>
    setSelectedClients(value || []);

  const handleChangePeriodo = (key: PeriodoKey, campo: 'inicio' | 'fim') => (data: any) =>
    setPeriodos((anterior) => ({
      ...anterior,
      [key]: { ...anterior[key], [campo]: data ? data.toISOString().split('T')[0] : '' },
    }));

  const periodoInvalido = ({ inicio, fim }: { inicio: string; fim: string }) =>
    inicio !== '' && fim !== '' && new Date(inicio) > new Date(fim);

  const montarRange = ({ inicio, fim }: { inicio: string; fim: string }) =>
    inicio === '' && fim === '' ? '' : `${inicio};${fim}`;

  const handleSubmit = async () => {
    keycloak.updateToken(300);

    if (selectedTenants.length === 0) {
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade de Negócio.' }));
      return;
    }

    const periodoComErro = PERIODOS.find(({ key }) => periodoInvalido(periodos[key]));
    if (periodoComErro) {
      dispatch(
        setErrorFeedback({
          message: `A data inicial de ${periodoComErro.label} não pode ser maior que a data final.`,
        })
      );
      return;
    }

    if (token && selectedUnit) {
      const erro = await generateProcessosExportacaoReport(token, selectedUnit, {
        tenantIds: selectedTenants.map((t) => t.id.toString()),
        clientIds: selectedClients.map((c) => c.id.toString()),
        dataRegistroDueRange: montarRange(periodos.registroDue),
        dataDesembaracoRange: montarRange(periodos.desembaraco),
        dataAverbacaoRange: montarRange(periodos.averbacao),
      });

      if (erro == null) {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
      } else {
        dispatch(setErrorFeedback({ message: erro }));
      }
    }

    history.push('/exportacao/relatorios');
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form onSubmit={() => {}}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={selectedTenants.length === 0}>
                    <MultiSelectStyled
                      controlledValues={selectedTenants}
                      options={tenantList.map((t: { id: { toString: () => any }; pessoa: { nomeResumido: any } }) => ({
                        id: t.id.toString(),
                        value: t.pessoa.nomeResumido,
                      }))}
                      onChangeAction={handleTenantsChange}
                      label="Unidade de Negócio"
                    />
                    {selectedTenants.length === 0 && (
                      <Typography variant="body2" color="error">
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedClients}
                      options={clientes.map((c) => ({
                        id: c.id ?? '',
                        value: `(${c.pessoa!.cnpj}) ${c.pessoa!.nome}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                {PERIODOS.map(({ key, label }) => (
                  <Grid item xs={12} container spacing={2} key={key}>
                    <Grid item xs={6}>
                      <Field
                        name={`${key}Inicio`}
                        render={({ input }) => (
                          <QCXFinalDatePickerField
                            id={`processos-exportacao-${key}-inicio`}
                            name={input.name}
                            label={`${label} (Início)`}
                            required={false}
                            format="DD/MM/YYYY"
                            placeholder="DD/MM/YYYY"
                            onChange={(d: any) => {
                              handleChangePeriodo(key, 'inicio')(d);
                              input.onChange(d);
                            }}
                            validate={undefined}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Field
                        name={`${key}Fim`}
                        render={({ input }) => (
                          <QCXFinalDatePickerField
                            id={`processos-exportacao-${key}-fim`}
                            name={input.name}
                            label={`${label} (Fim)`}
                            required={false}
                            format="DD/MM/YYYY"
                            placeholder="DD/MM/YYYY"
                            onChange={(d: any) => {
                              handleChangePeriodo(key, 'fim')(d);
                              input.onChange(d);
                            }}
                            validate={undefined}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={4}>
          <Button variant="outlined" color="primary" onClick={closeModal}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            fullWidth
            disabled={selectedTenants.length === 0}
          >
            Gerar Relatório
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessosExportacaoPage;
